import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mpApi, SERVICE_KEYS, serviceMeta } from "./mpShared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { client_name: "", company_name: "", contact_person_name: "", contact_phone: "", contact_email: "", city: "", client_status: "active", notes: "" };
const noTargets = () => Object.fromEntries(SERVICE_KEYS.map((k) => [k, 0]));

/** Create a CRM client and (optionally) its default monthly service targets in one step. */
export default function ClientDialog({ open, onClose, businessId, canManage, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [targets, setTargets] = useState(noTargets);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm(EMPTY); setTargets(noTargets()); } }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = SERVICE_KEYS.reduce((s, k) => s + (Number(targets[k]) || 0), 0);

  const save = async () => {
    const name = form.client_name.trim();
    if (!name) return toast.error("Client name is required");
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, client_name: name, business_id: businessId || undefined }),
      });
      const client = await res.json().catch(() => ({}));
      if (!res.ok || !client.client_id) throw new Error(client.detail || "Could not add the client");
      if (canManage && total > 0) {
        try {
          await mpApi("/targets", { method: "PUT", body: { client_id: client.client_id, targets, scope: "default", business_id: businessId || undefined } });
        } catch (e) {
          toast.error(`Client added, but targets were not saved: ${e.message}`);
          onCreated?.(client); onClose(); return;
        }
      }
      toast.success(total > 0 && canManage ? `${name} added with ${total} deliverables a month` : `${name} added`);
      onCreated?.(client);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input value={form[key]} onChange={(e) => set(key, e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900" {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle>Add new client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {field("client_name", "Client name *", { autoFocus: true, placeholder: "e.g. Rohilkhand Hospital", "data-testid": "mp-client-name" })}
            <div>
              <label className="text-xs font-medium text-slate-600">Status</label>
              <select value={form.client_status} onChange={(e) => set("client_status", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                <option value="active">Active</option><option value="inactive">Inactive</option><option value="foc">FOC</option>
              </select>
            </div>
            {field("company_name", "Company")}
            {field("city", "City")}
            {field("contact_person_name", "Contact person")}
            {field("contact_phone", "Phone", { inputMode: "tel" })}
            {field("contact_email", "Email", { type: "email" })}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          {canManage && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">Monthly targets <span className="font-normal text-slate-500">(optional)</span></span>
                <span className="text-xs text-slate-500">{total} deliverable{total === 1 ? "" : "s"} / month</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SERVICE_KEYS.map((k) => (
                  <label key={k} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: serviceMeta(k).color }} />{serviceMeta(k).label}</span>
                    <input type="number" min="0" max="999" value={targets[k]} data-testid={`mp-new-target-${k}`}
                      onChange={(e) => setTargets((t) => ({ ...t, [k]: Math.max(0, Math.min(999, Number(e.target.value) || 0)) }))}
                      className="h-8 w-14 rounded-md border border-slate-200 bg-white text-center text-sm tabular-nums" />
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-500">Applies to every month; change it later in Service Targets. Use “Generate Monthly Tasks” to create the tasks.</p>
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mp-client-save" className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
